import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  combineLatest,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { ISidebarContent } from '../../../gui/progress-sidebar/progress-sidebar.component';
import { PatternApiService } from 'src/app/services/api/pattern-api.service';
import { ApiPattern } from 'src/app/models_new/classes/api-models/ApiPattern';
import { SimulationApiService } from 'src/app/services/api/simulation-api.service';
import { DialogService } from 'src/app/services/dialog.service';
import { NewSimpleSimulationComponent } from 'src/app/components/dialogs/new-simple-simulation/new-simple-simulation.component';
import { DialogSize } from 'src/app/models_new/enums/dialogSize';
import { pagesPATH } from 'src/app/models_new/config/pages';
import { StateService } from 'src/app/auth/state.service';
import { SimulationStatus } from 'src/app/models_new/enums/simulation-status';
import { IApiRobotConfiguration } from 'src/app/models_new/classes/api-models/ApiRobotConfiguration';
import { NotificationService } from 'src/app/services/notification.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { ApiOrganization } from 'src/app/models_new/classes/api-models/ApiOrganization';
import { RobotConfigApiService } from 'src/app/services/api/robot-config-api.service';
import { LocalStorageKey } from 'src/app/models_new/enums/local-storage-keys';
import { LocalStorageService } from 'src/app/services/local-storage.service';

@Component({
  selector: 'app-simple-simulation-container-component',
  templateUrl: './simple-simulation-container.component.html',
  styleUrls: ['./simple-simulation-container.component.scss'],
})
export class SimpleSimulationContainerComponent implements OnInit {
  step: number = 0;
  pattern$: Observable<ApiPattern>;
  robotConfigs$: Observable<IApiRobotConfiguration[]>;
  selectedRobotConfigs: IApiRobotConfiguration[] = [];
  nextDisabled$: Observable<boolean>;

  pages: ISidebarContent[] = [
    {
      id: 0,
      header: '1/3 - Verify Pattern Configuration',
    },
    {
      id: 1,
      header: '2/3 - Robot Configuration',
    },
    {
      id: 2,
      header: '3/3 - Summary',
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private patternApi: PatternApiService,
    private robotConfigApi: RobotConfigApiService,
    private simulationApi: SimulationApiService,
    private dialogService: DialogService,
    private router: Router,
    private stateService: StateService,
    private notify: NotificationService,
    private errorHandler: ErrorHandlerService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit() {
    this.robotConfigs$ = this.stateService
      .getCustomerOrSalesOrganizationPreserveState()
      .pipe(
        switchMap((org: ApiOrganization) =>
          combineLatest([
            this.robotConfigApi.fetchRobotconfigs(org.id),
            this.robotConfigApi.fetchSalesOrgRobotConfigurations(org.id),
          ])
        ),
        map(([configs, sales_configs]) => {
          return configs.concat(sales_configs);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.pattern$ = this.patternApi
      .fetchPatternById(this.route.snapshot.params.id)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  backClick() {
    this.step = this.step - 1;
  }

  nextClick() {
    if (this.step === 1 && !this.selectedRobotConfigs.length) {
      this.notify.showError('Please select a robot configuration');
    } else {
      this.step++;
    }
  }

  runClick() {
    const dialogRef = this.dialogService.showCustomDialog(
      NewSimpleSimulationComponent,
      DialogSize.SMALL,
      null,
      {
        simulation: {
          name: '',
        },
      }
    );

    dialogRef
      .afterClosed()
      .pipe(
        // Proceed if user has written name and pressed "make simulation"
        filter(Boolean),
        switchMap((dialog_val) => {
          return forkJoin([
            this.pattern$.pipe(take(1)),
            of(dialog_val[0].value).pipe(take(1)),
            this.stateService.getCustomerOrSalesOrganization().pipe(take(1)),
          ]);
        }),
        // Construct data structure and insert simulation
        switchMap(([pattern, name, org]) => {
          let simulations = [];
          for (let rc of this.selectedRobotConfigs) {
            simulations.push({
              name: `${name} - ${rc.name}`,
              pattern_id: pattern.id,
              scene_id: rc.scene.id,
              strategy_id: rc.strategy.id,
              simulation_state: SimulationStatus.QUEUED,
              metadata: {
                unitSystem:
                  this.localStorageService.getData(
                    LocalStorageKey.PREFERRED_UNIT_SYSTEM
                  ) || 'metric',
              },
            });
          }
          return this.simulationApi.insertSimulationBatch(simulations, org.id);
        })
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.router.navigate([pagesPATH.SIMULATIONS]);
          } else {
            this.errorHandler.handleError(
              new Error('Create simulation failed')
            );
          }
        },
        error: (err) => {
          this.errorHandler.handleError(err);
        },
      });
  }
}
